import React, { useContext, useEffect, useState } from 'react'

import styled from '@emotion/styled'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { BusinessIcon, CashAppPayIcon, TransferP2PIcon } from '@arcade/web/icons/react'
import { Button, PaddingInset, spacing, Stack } from '@cash-design-system/react'
import { ClientScenario } from '@cash-web/protos/squareup/franklin/api/client_scenario.pb'
import { RatePlanBlocker } from '@cash-web/protos/squareup/franklin/blockers/rate_plan_blocker.pb'
import { RatePlan } from '@cash-web/protos/squareup/franklin/rate_plan.pb'
import { RatePlanBlockerSupplement } from '@cash-web/protos/squareup/franklin/scenarios.pb'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationSetRatePlan } from '@cash-web/shared-data-access-api/react-query/useMutationSetRatePlan'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { RadioGroup } from '@cash-web/shared-feature-form'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import CheckmarkChecked from '../../assets/icons/CheckmarkChecked'
import CheckmarkUnchecked from '../../assets/icons/CheckmarkUnchecked'
import { BaseBlocker, BaseBlockerFormFields, BlockerProps, SubmitButton } from '../BaseBlocker/BaseBlocker'

interface Props extends BlockerProps {
  blocker: RatePlanBlocker
  supplement?: RatePlanBlockerSupplement
}

interface FormFields extends BaseBlockerFormFields {
  ratePlan: RatePlan
}

const PlanChoices = styled.div``

const PlanTitle = styled.h2`
  display: flex;
  justify-content: space-between;
  ${({ theme }) => theme.typography.button};
`

const PlanDescription = styled.p`
  ${({ theme }) => theme.typography.helpText};
  color: ${({ theme }) => theme.colors.body};
`

const Feature = styled.li`
  display: flex;
  margin-bottom: ${({ theme }) => theme.spacing.l};
  width: 100%;
  ${({ theme }) => theme.typography.body};

  &:last-child {
    margin-bottom: 0;
  }
`

const IconWrapper = styled.div`
  flex: 0 0 auto;
`

const FeatureDescription = styled.p`
  margin-left: ${({ theme }) => theme.spacing.m};
`

export const DEFAULT_RATE_PLANS = [RatePlan.PERSONAL, RatePlan.BUSINESS]

const CheckmarkIcon = styled.div`
  position: absolute;
  right: 1rem;
  color: ${({ theme }) => theme.colors.primary};
`

export const BlockerRatePlan: React.FC<Props> = ({ blocker, supplement, skippable }) => {
  const { t } = useTranslation()
  const form = useForm<FormFields>({ mode: 'onTouched' })
  const {
    watch,
    formState: { isSubmitting },
  } = form
  const { clearError } = useContext(ErrorContext)
  const { handleBlockerResponse } = useBlockerResponseHandler()
  const flow = useFlowContext()
  const { mutateAsync: setRatePlan } = useMutationSetRatePlan({
    options: {
      clientScenario: flow.active?.clientScenario,
      flowToken: flow.active?.flowToken,
    },
  })
  // The confirm step is a 2nd page in the rate plan blocker that provides extra information.
  // The 2nd page is only shown when a user selects the business rate plan and has ClientScenario.ONBOARDING,
  // otherwise they just select the business rate plan and continue logging in
  const [isOnConfirmStep, setIsOnConfirmStep] = useState(false)
  const watchRatePlan = watch('ratePlan')

  useEffect(() => {
    clearError()
    // Disable warning here because we only want to trigger this hook when a user selects a different
    // rate plan. Adding all the depenendencies will never show the apiError
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchRatePlan])

  const isOnboarding = flow.active?.clientScenario === ClientScenario.ONBOARDING
  const title = isOnConfirmStep
    ? t('Cash for Business', { context: 'title' })
    : t('What do you primarily use Cash App for?', { context: 'title' })
  const subtitle = isOnConfirmStep
    ? undefined
    : t('This helps us know what you’ll need from your account.', { context: 'subtitle' })
  let availableRatePlans = DEFAULT_RATE_PLANS
  if (blocker.available_rate_plans && blocker.available_rate_plans.length > 0) {
    availableRatePlans = blocker.available_rate_plans
  }

  interface RatePlanChoice {
    ratePlan: RatePlan
    name: string
    description: string
  }

  const ratePlanChoices: RatePlanChoice[] = availableRatePlans
    .filter(ratePlan => ratePlan.toLowerCase() === 'business' || ratePlan.toLowerCase() === 'personal')
    .map(ratePlan => {
      if (ratePlan.toLowerCase() === 'business') {
        return {
          ratePlan,
          name: t('Business', {
            context: "one of the two options for Cash App's rate plan, the other being the personal plan",
          }),
          description: isOnboarding
            ? t('Getting paid for services and goods', { context: 'description' })
            : t('Get paid for goods and services. Free to send, just {rate} to receive.', {
                context: 'description',
                // eslint-disable-next-line @nx/workspace/i18n-require-context
                rate: t('2.75%'), // TODO: this 2.75% should be number formatted by locale, not translated
              }),
        }
      } else {
        return {
          ratePlan,
          name: t('Personal', {
            context: "one of the two options for Cash App's rate plan, the other being the business plan",
          }),
          description: isOnboarding
            ? t('Exchanging money with friends', { context: 'description' })
            : t('Exchange money with friends and family for free.', {
                context: 'description',
              }),
        }
      }
    })

  const handleBackClick = () => {
    setIsOnConfirmStep(false)
  }

  const onSubmit = async ({ ratePlan }: FormFields) => {
    if (isOnboarding && ratePlan === RatePlan.BUSINESS && !isOnConfirmStep) {
      setIsOnConfirmStep(true)
      return
    }
    // https://prototype.sqprod.co/#/docs/app.SetRatePlanRequest
    const payload = {
      rate_plan: ratePlan,
      commit: true,
    }

    await handleBlockerResponse({
      promisedBlockerUpdate: setRatePlan({ ...payload, payment_tokens: flow.active?.paymentTokens }),
      defaultError: t('Sorry, an error occurred and that plan was not selected. Please try again.', {
        context: 'this is the default error message when setting the rate plan errors',
      }),
    })
  }

  const buttons = isOnConfirmStep
    ? [
        <Button size="large" variant="secondary" onClick={handleBackClick} key="rate-plan-back">
          {t('Back', { context: 'button text' })}
        </Button>,
        <Button size="large" loading={isSubmitting} type="submit" key="rate-plan-confirm">
          {t('Confirm', { context: 'button CTA to submit a form' })}
        </Button>,
      ]
    : [<SubmitButton key="rate-plan-next" />]

  return (
    <BaseBlocker
      mainText={title}
      secondaryText={subtitle}
      form={form}
      onSubmit={onSubmit}
      buttons={buttons}
      skippable={skippable}
    >
      {!isOnConfirmStep && ratePlanChoices ? (
        <PlanChoices>
          <RadioGroup
            name="ratePlan"
            rules={{
              required: {
                value: true,
                message: t('Select a type of Cash account', { context: 'error message' }),
              },
            }}
            options={ratePlanChoices.map(({ ratePlan, name, description }) => {
              const isSelectedRatePlan = watchRatePlan === ratePlan
              return {
                label: (
                  <Stack space={spacing.xxs}>
                    <PlanTitle>
                      {name}
                      <CheckmarkIcon>
                        {isSelectedRatePlan ? <CheckmarkChecked /> : <CheckmarkUnchecked />}
                      </CheckmarkIcon>
                    </PlanTitle>
                    <PlanDescription>{description}</PlanDescription>
                  </Stack>
                ),
                value: ratePlan,
              }
            })}
          />
        </PlanChoices>
      ) : (
        <PaddingInset>
          <ul>
            <Feature>
              <IconWrapper>
                <BusinessIcon size={24} variant="subtle" />
              </IconWrapper>
              <FeatureDescription>
                {t('Accept payments for your business', { context: 'description' })}
              </FeatureDescription>
            </Feature>
            <Feature>
              <IconWrapper>
                <TransferP2PIcon size={24} variant="subtle" />
              </IconWrapper>
              <FeatureDescription>
                {t('Free to send and just {rate} to receive', {
                  context: 'description',
                  // eslint-disable-next-line @nx/workspace/i18n-require-context
                  rate: t('2.75%'), // TODO: this 2.75% should be number formatted by locale, not translated
                })}
              </FeatureDescription>
            </Feature>
            <Feature>
              <IconWrapper>
                <CashAppPayIcon size={24} variant="subtle" />
              </IconWrapper>
              <FeatureDescription>
                {t('Higher limits on how much you can receive', { context: 'description' })}
              </FeatureDescription>
            </Feature>
          </ul>
        </PaddingInset>
      )}
    </BaseBlocker>
  )
}
